import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ArrowLeft, Calculator, TrendingUp, Info, Percent, AlertTriangle } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const STORAGE_KEY = 'gst_calculator_data';

const GST_RATES = [
  { value: '0', label: '0% (Exempted)' },
  { value: '0.25', label: '0.25% (Rough Precious & Semi-Precious Stones)' },
  { value: '3', label: '3% (Gold & Precious Stones)' },
  { value: '5', label: '5% (Essential Goods)' },
  { value: '12', label: '12% (Standard Items)' },
  { value: '18', label: '18% (Most Goods & Services)' },
  { value: '28', label: '28% (Luxury & Sin Goods)' }
];

export const GSTCalculator = () => {
  const [originalAmount, setOriginalAmount] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).originalAmount : 10000;
  });
  
  const [gstRate, setGstRate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).gstRate : '18';
  });
  
  const [gstType, setGstType] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).gstType : 'exclusive';
  });

  const [transactionType, setTransactionType] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).transactionType : 'intrastate';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      originalAmount,
      gstRate,
      gstType,
      transactionType
    }));
  }, [originalAmount, gstRate, gstType, transactionType]);

  const calculateGST = () => {
    const amount = parseFloat(originalAmount) || 0;
    const rate = parseFloat(gstRate) || 0;

    let baseAmount, gstAmount, finalPrice, cgst, sgst, igst;

    if (gstType === 'exclusive') {
      baseAmount = amount;
      gstAmount = (amount * rate) / 100;
      finalPrice = amount + gstAmount;
    } else {
      finalPrice = amount;
      baseAmount = (amount * 100) / (100 + rate);
      gstAmount = amount - baseAmount;
    }

    if (transactionType === 'intrastate') {
      cgst = gstAmount / 2;
      sgst = gstAmount / 2;
      igst = 0;
    } else {
      cgst = 0;
      sgst = 0;
      igst = gstAmount;
    }

    return {
      baseAmount: Math.round(baseAmount * 100) / 100,
      gstAmount: Math.round(gstAmount * 100) / 100,
      finalPrice: Math.round(finalPrice * 100) / 100,
      cgst: Math.round(cgst * 100) / 100,
      sgst: Math.round(sgst * 100) / 100,
      igst: Math.round(igst * 100) / 100,
      effectiveRate: rate
    };
  };

  const generatePriceComparison = () => {
    const amount = parseFloat(originalAmount) || 0;
    
    return GST_RATES.map(rate => {
      const rateValue = parseFloat(rate.value);
      const gstAmount = gstType === 'exclusive' 
        ? (amount * rateValue) / 100
        : amount - (amount * 100) / (100 + rateValue);
      const finalPrice = gstType === 'exclusive'
        ? amount + gstAmount
        : amount;
      const baseAmount = gstType === 'exclusive'
        ? amount
        : finalPrice - gstAmount;
      
      return {
        rate: `${rateValue}%`,
        baseAmount: Math.round(baseAmount),
        gstAmount: Math.round(gstAmount),
        finalPrice: Math.round(finalPrice)
      };
    });
  };

  const generateBusinessImpact = () => {
    const results = calculateGST();
    const monthlyRevenue = results.finalPrice * 100; // Assuming 100 transactions
    const yearlyRevenue = monthlyRevenue * 12;
    const yearlyGST = results.gstAmount * 100 * 12;

    return {
      monthlyRevenue: Math.round(monthlyRevenue),
      yearlyRevenue: Math.round(yearlyRevenue),
      yearlyGST: Math.round(yearlyGST),
      transactions: 100
    };
  };

  const generateRateImpact = () => {
    const amount = parseFloat(originalAmount) || 0;
    const rates = [5, 12, 18, 28];
    
    return rates.map(rate => {
      const gstAmount = gstType === 'exclusive' 
        ? (amount * rate) / 100
        : amount - (amount * 100) / (100 + rate);
      const finalPrice = gstType === 'exclusive'
        ? amount + gstAmount
        : amount;
      
      return {
        rate: `${rate}%`,
        gstAmount: Math.round(gstAmount),
        finalPrice: Math.round(finalPrice)
      };
    });
  };

  const results = calculateGST();
  const priceComparison = generatePriceComparison();
  const businessImpact = generateBusinessImpact();
  const rateImpact = generateRateImpact();

  const pieData = [
    { name: 'Base Amount', value: results.baseAmount, color: 'hsl(var(--chart-1))' },
    { name: 'GST', value: results.gstAmount, color: 'hsl(var(--chart-2))' }
  ];

  const taxSplitData = transactionType === 'intrastate' 
    ? [
        { name: 'CGST', value: results.cgst, color: 'hsl(var(--chart-3))' },
        { name: 'SGST', value: results.sgst, color: 'hsl(var(--chart-4))' }
      ]
    : [
        { name: 'IGST', value: results.igst, color: 'hsl(var(--chart-5))' }
      ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))'
  ];

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <Percent className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  GST Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Guide
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is GST?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  GST (Goods and Services Tax) is an indirect tax levied on the supply of goods and services in India. 
                  It has replaced multiple indirect taxes like VAT, Service Tax, and Excise Duty. GST is a destination-based 
                  tax that is charged at every stage of the supply chain.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">GST Details</CardTitle>
                  <CardDescription>Enter transaction information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="originalAmount" className="text-sm font-medium">
                      Amount ($)
                    </Label>
                    <Input
                      id="originalAmount"
                      type="number"
                      value={originalAmount}
                      onChange={(e) => setOriginalAmount(e.target.value)}
                      placeholder="10000"
                      step="0.01"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">
                      {gstType === 'exclusive' ? 'Amount before GST' : 'Final amount including GST'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gstRate" className="text-sm font-medium">
                      GST Rate
                    </Label>
                    <Select value={gstRate} onValueChange={setGstRate}>
                      <SelectTrigger id="gstRate">
                        <SelectValue placeholder="Select GST Rate" />
                      </SelectTrigger>
                      <SelectContent>
                        {GST_RATES.map((rate) => (
                          <SelectItem key={rate.value} value={rate.value}>
                            {rate.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">GST Type</Label>
                    <RadioGroup value={gstType} onValueChange={setGstType}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="exclusive" id="exclusive" />
                        <Label htmlFor="exclusive" className="font-normal cursor-pointer">
                          GST Exclusive (Add GST to amount)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="inclusive" id="inclusive" />
                        <Label htmlFor="inclusive" className="font-normal cursor-pointer">
                          GST Inclusive (Remove GST from amount)
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Transaction Type</Label>
                    <RadioGroup value={transactionType} onValueChange={setTransactionType}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="intrastate" id="intrastate" />
                        <Label htmlFor="intrastate" className="font-normal cursor-pointer">
                          Intra-State (CGST + SGST)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="interstate" id="interstate" />
                        <Label htmlFor="interstate" className="font-normal cursor-pointer">
                          Inter-State (IGST)
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setOriginalAmount(originalAmount)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate GST
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">GST Calculation Results</CardTitle>
                    <CardDescription>Your tax breakdown</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Base Amount</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(results.baseAmount)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Amount before tax</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <Percent className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">GST Amount</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.gstAmount)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">at {gstRate}% GST</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-accent-foreground" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Final Price</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent-foreground">
                          {formatCurrency(results.finalPrice)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Total amount payable</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-purple-light to-card border border-purple/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-purple flex items-center justify-center">
                            <Info className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Effective Rate</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-purple">
                          {results.effectiveRate}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Applied GST rate</p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <h3 className="text-lg font-display font-semibold mb-4">Tax Component Breakdown</h3>
                      <div className="grid sm:grid-cols-3 gap-4">
                        {transactionType === 'intrastate' ? (
                          <>
                            <div>
                              <p className="text-sm text-muted-foreground mb-1">CGST ({gstRate / 2}%)</p>
                              <p className="text-xl font-bold text-foreground">{formatCurrency(results.cgst)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground mb-1">SGST ({gstRate / 2}%)</p>
                              <p className="text-xl font-bold text-foreground">{formatCurrency(results.sgst)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground mb-1">Total GST</p>
                              <p className="text-xl font-bold text-secondary">{formatCurrency(results.gstAmount)}</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <p className="text-sm text-muted-foreground mb-1">IGST ({gstRate}%)</p>
                              <p className="text-xl font-bold text-foreground">{formatCurrency(results.igst)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground mb-1">Total GST</p>
                              <p className="text-xl font-bold text-secondary">{formatCurrency(results.gstAmount)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground mb-1">Transaction Type</p>
                              <p className="text-sm font-semibold text-foreground">Inter-State</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="text-2xl font-display">Price Before vs After GST</CardTitle>
                  <CardDescription className="text-base">
                    Visual breakdown of your transaction
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Tax Component Split</CardTitle>
                  <CardDescription>
                    {transactionType === 'intrastate' ? 'CGST and SGST breakdown' : 'IGST breakdown'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={taxSplitData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {taxSplitData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="font-display">GST Rate Comparison</CardTitle>
                <CardDescription>
                  See how different GST rates affect the final price
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={rateImpact}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="rate" 
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Bar dataKey="gstAmount" fill="hsl(var(--chart-2))" name="GST Amount" />
                      <Bar dataKey="finalPrice" fill="hsl(var(--chart-1))" name="Final Price" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding GST in India</CardTitle>
                <CardDescription className="text-base">
                  Comprehensive guide to GST calculation and compliance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    What is CGST, SGST, and IGST?
                  </h3>
                  <div className="space-y-3">
                    <div className="p-4 rounded-lg bg-primary-light border border-primary/20">
                      <h4 className="font-semibold text-foreground mb-2">CGST (Central GST)</h4>
                      <p className="text-muted-foreground text-sm">
                        Tax collected by the Central Government on intra-state transactions. In your case: <strong>{formatCurrency(results.cgst)}</strong>
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border border-secondary/20">
                      <h4 className="font-semibold text-foreground mb-2">SGST (State GST)</h4>
                      <p className="text-muted-foreground text-sm">
                        Tax collected by the State Government on intra-state transactions. In your case: <strong>{formatCurrency(results.sgst)}</strong>
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border border-accent/20">
                      <h4 className="font-semibold text-foreground mb-2">IGST (Integrated GST)</h4>
                      <p className="text-muted-foreground text-sm">
                        Tax collected by the Central Government on inter-state transactions. In your case: <strong>{formatCurrency(results.igst)}</strong>
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Business vs Consumer Impact
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Understanding how GST affects different stakeholders in the transaction chain:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-secondary-light border border-secondary/20">
                      <h4 className="font-semibold text-foreground mb-2">For Businesses</h4>
                      <ul className="text-sm text-muted-foreground space-y-2">
                        <li>✅ Can claim Input Tax Credit (ITC)</li>
                        <li>✅ GST paid on purchases can be offset</li>
                        <li>✅ Only net tax liability needs to be paid</li>
                        <li>📊 Monthly/Quarterly filing required</li>
                      </ul>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border border-accent/20">
                      <h4 className="font-semibold text-foreground mb-2">For Consumers</h4>
                      <ul className="text-sm text-muted-foreground space-y-2">
                        <li>💰 GST is borne by end consumer</li>
                        <li>💰 No ITC available to claim</li>
                        <li>📜 Always check GST-inclusive pricing</li>
                        <li>🧾 Request proper tax invoices</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent-foreground">
                    Pricing Strategy Tips
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Display Inclusive Pricing</h4>
                      <p className="text-muted-foreground">
                        Show final GST-inclusive prices to customers. Your product: <strong>{formatCurrency(results.baseAmount)}</strong> + 
                        GST = <strong>{formatCurrency(results.finalPrice)}</strong>
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Competitive Positioning</h4>
                      <p className="text-muted-foreground">
                        Consider GST rates when pricing products. Lower GST categories (5%, 12%) offer competitive advantages.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Margin Calculation</h4>
                      <p className="text-muted-foreground">
                        Always calculate margins on base price, not GST-inclusive price. Base: {formatCurrency(results.baseAmount)}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Common GST Mistakes to Avoid
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Wrong GST Rate:</strong> Using incorrect rate for your product 
                        category can lead to penalties. Verify HSN/SAC codes.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Inclusive vs Exclusive Confusion:</strong> Always clarify if 
                        prices are inclusive or exclusive of GST. Current selection: <strong>{gstType}</strong>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">CGST/SGST vs IGST Error:</strong> Use CGST+SGST for intra-state 
                        and IGST for inter-state. You selected: <strong>{transactionType}</strong>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Late Filing:</strong> File GSTR returns on time to avoid 
                        late fees ($50/day for nil returns, more for others).
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    GST Registration Threshold
                  </h3>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">For Goods Suppliers</p>
                        <p className="text-2xl font-bold text-secondary">$40 Lakhs</p>
                        <p className="text-xs text-muted-foreground mt-1">($20 Lakhs for special category states)</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">For Service Providers</p>
                        <p className="text-2xl font-bold text-secondary">$20 Lakhs</p>
                        <p className="text-xs text-muted-foreground mt-1">($10 Lakhs for special category states)</p>
                      </div>
                      <div className="pt-3 border-t border-border/50">
                        <p className="text-sm text-muted-foreground">
                          <strong className="text-foreground">Note:</strong> Mandatory registration regardless of threshold 
                          for inter-state supply, e-commerce operators, and specified categories.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default GSTCalculator;
